import React, { FC, useMemo } from 'react';
import { convertBarChartToECharts } from '../adapters/echartsAdapter';
import { EChartsContainer, useSeriesChartEvents } from '../shared';
import { BarChartProps } from '../types';

/**
 * ScalarBarChart
 *
 * A generic bar chart component with a library-agnostic API.
 * Internally uses ECharts, but the API is designed to be easily swappable.
 *
 * @example
 * ```tsx
 * <ScalarBarChart
 *   series={[
 *     {
 *       id: 'series1',
 *       label: 'Sales',
 *       data: [
 *         { x: 'Jan', y: 100 },
 *         { x: 'Feb', y: 200 },
 *       ],
 *       color: '#3498db',
 *     },
 *   ]}
 *   config={{
 *     title: 'Monthly Sales',
 *     xAxis: { label: 'Month' },
 *     yAxis: { label: 'Amount' },
 *   }}
 * />
 * ```
 */
const ScalarBarChart: FC<BarChartProps> = ({
  series,
  categoryOrder,
  config,
  stacked = false,
  horizontal = false,
  barWidth,
  barCategoryGap,
  barGap,
  preserveCategoryOrder = false,
  className,
  style,
  loading = false,
  error = null,
  notMerge = false,
  onDataPointClick,
  onSeriesClick,
  onChartReady,
}) => {
  const echartsOption = useMemo(
    () =>
      convertBarChartToECharts({
        series,
        categoryOrder,
        config,
        stacked,
        horizontal,
        barWidth,
        barCategoryGap,
        barGap,
        preserveCategoryOrder,
      }),
    [series, categoryOrder, config, stacked, horizontal, barWidth, barCategoryGap, barGap, preserveCategoryOrder]
  );

  const onEvents = useSeriesChartEvents(series, onDataPointClick, onSeriesClick);

  return (
    <EChartsContainer
      option={echartsOption}
      notMerge={notMerge}
      className={className}
      style={style}
      error={error}
      loading={loading}
      onEvents={onEvents}
      onChartReady={onChartReady}
    />
  );
};

export default ScalarBarChart;
